/**
 * The demo tour (API api/demoTour): a new sign up looks around Zenith Consulting Group,
 * read only, for 7 days. This browser is "on the tour" while the flag is set; every API
 * call then carries DEMO_TOUR_HEADER and the API answers as the tour's person.
 *
 * The header only asks for the tour - it names nobody. Who the tour is, and whether
 * this person's tour is still running, is the API's alone.
 *
 * No imports from api.service: it reads getDemoTourHeaders from here.
 */

export const DEMO_TOUR_HEADER = "x-mx-demo-tour"
export const DEMO_TOUR_CODES = {
    READ_ONLY: "demoTour/read-only",
    NOT_IN_TOUR: "demoTour/not-in-tour",
    ENDED: "demoTour/ended",
    NOT_ELIGIBLE: "demoTour/not-eligible",
    UNAVAILABLE: "demoTour/unavailable",
}

// Ours, for testing (API DEMO_TOUR.TESTER_EMAILS): Home asks about the tour for them
// even though they have orgs. Whether they may start one is still the API's call.
const DEMO_TOUR_TESTER_EMAILS = ["makis-2@29kamden.com"]

const STORAGE_KEY = "mx-demo-tour"

/** @return {{expiresAt: String}|null} The running tour this browser is on */
export function getDemoTour() {
    try {
        const tour = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null")

        if (!tour?.expiresAt || new Date(tour.expiresAt).getTime() <= Date.now()) {
            if (tour) window.localStorage.removeItem(STORAGE_KEY)
            return null
        }

        return tour
    } catch (e) {
        return null
    }
}

export function isDemoTourOn() {
    return Boolean(getDemoTour())
}

export function isDemoTourTester(person) {
    return DEMO_TOUR_TESTER_EMAILS.includes(String(person?.email || "").toLowerCase())
}

export function setDemoTour(tour) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ expiresAt: tour.expiresAt }))
}

export function clearDemoTour() {
    window.localStorage.removeItem(STORAGE_KEY)
}

/** Whole days left, at least 1 while it runs. */
export function getDemoTourDaysLeft() {
    const tour = getDemoTour()

    if (!tour) return 0

    return Math.max(1, Math.ceil((new Date(tour.expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
}

export function getDemoTourHeaders() {
    return isDemoTourOn() ? { [DEMO_TOUR_HEADER]: "1" } : {}
}

/** Back to the person's own missionX: a full reload, so nothing of the tour stays in memory. */
export function leaveDemoTour() {
    clearDemoTour()
    window.location.assign("/")
}

/**
 * Called with every failed API call. A refused write says nothing (the pill already
 * says read only, and background saves would raise it unasked); a tour that ran out,
 * or that cannot run right now, takes the person back to their own (the tour itself
 * keeps running: View opens it again).
 */
export function handleDemoTourError(err) {
    if (!isDemoTourOn()) return

    const code = err?.response?.data?.code

    if (code === DEMO_TOUR_CODES.ENDED || code === DEMO_TOUR_CODES.UNAVAILABLE) {
        leaveDemoTour()
    }
}
